import { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, XCircle, RefreshCw } from "lucide-react";
import { getConverterApiBase } from "../api";
import TopHeader from "../components/TopHeader";
import Footer from "../components/Footer";

type ComponentStatus = {
  name: string;
  status: "operational" | "degraded" | "down";
  message?: string;
  loaded_servers?: number;
};

type StatusData = {
  status: "operational" | "degraded" | "down";
  timestamp: string;
  components: ComponentStatus[];
};

function StatusBadge({ status }: { status: string }) {
  if (status === "operational") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-100 dark:bg-green-950/40 text-green-700 dark:text-green-400 text-xs font-semibold">
        <CheckCircle2 className="w-3.5 h-3.5" />
        Operational
      </span>
    );
  }
  if (status === "degraded") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 text-xs font-semibold">
        <AlertTriangle className="w-3.5 h-3.5" />
        Degraded
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-400 text-xs font-semibold">
      <XCircle className="w-3.5 h-3.5" />
      Down
    </span>
  );
}

export default function StatusPage() {
  const [data, setData] = useState<StatusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  const fetchStatus = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await fetch(`${getConverterApiBase()}/api/status`, { cache: "no-store" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json() as StatusData;
      setData(json);
      setLastChecked(new Date());
    } catch {
      setError(true);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchStatus();
    const id = setInterval(() => void fetchStatus(), 60_000);
    return () => clearInterval(id);
  }, []);

  const overallStatus = error ? "down" : (data?.status ?? "operational");

  const heroBg =
    overallStatus === "operational"
      ? "from-green-500 to-emerald-600"
      : overallStatus === "degraded"
      ? "from-amber-400 to-orange-500"
      : "from-red-500 to-rose-600";

  const heroText =
    overallStatus === "operational"
      ? "All Systems Operational"
      : overallStatus === "degraded"
      ? "Partial Degradation"
      : "Service Disruption";

  const heroSub =
    overallStatus === "operational"
      ? "Everything is running smoothly."
      : overallStatus === "degraded"
      ? "Some components are experiencing issues."
      : "We're aware and working on it.";

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <TopHeader />
      <main className="flex-1">
        {/* Hero */}
        <div className={`bg-gradient-to-r ${heroBg} py-14 px-6 text-center`}>
          <h1 className="text-3xl font-bold text-white mb-2">{heroText}</h1>
          <p className="text-white/80 text-sm">{heroSub}</p>
          {lastChecked && (
            <p className="text-white/60 text-xs mt-2">
              Last checked: {lastChecked.toLocaleTimeString()}
            </p>
          )}
        </div>

        <div className="max-w-2xl mx-auto px-4 py-10">
          {/* Refresh button */}
          <div className="flex justify-end mb-6">
            <button
              type="button"
              onClick={() => void fetchStatus()}
              disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-border text-sm font-medium hover:bg-muted transition-colors disabled:opacity-60"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              {loading ? "Checking…" : "Refresh"}
            </button>
          </div>

          {/* Components */}
          <div className="rounded-2xl border border-border overflow-hidden">
            <div className="px-5 py-3 bg-muted/50 border-b border-border">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Components
              </p>
            </div>

            {loading && !data && (
              <div className="px-5 py-8 text-center text-sm text-muted-foreground">
                Checking status…
              </div>
            )}

            {error && (
              <div className="px-5 py-8 text-center">
                <p className="text-sm font-medium text-red-600 dark:text-red-400">
                  Could not reach the API server.
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  The service may be starting up (Render free tier spins down after inactivity).
                </p>
              </div>
            )}

            {data?.components.map((c, i) => (
              <div
                key={c.name}
                className={`flex items-center justify-between px-5 py-4 ${
                  i < data.components.length - 1 ? "border-b border-border" : ""
                }`}
              >
                <div>
                  <p className="text-sm font-medium text-foreground">{c.name}</p>
                  {c.message && (
                    <p className="text-xs text-muted-foreground mt-0.5">{c.message}</p>
                  )}
                  {c.loaded_servers !== undefined && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {c.loaded_servers} server{c.loaded_servers !== 1 ? "s" : ""} loaded
                    </p>
                  )}
                </div>
                <StatusBadge status={c.status} />
              </div>
            ))}
          </div>

          {/* Uptime note */}
          <p className="text-center text-xs text-muted-foreground mt-8">
            Status refreshes every 60 seconds. For incidents, check{" "}
            <a
              href="https://render.com/status"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              render.com/status
            </a>
            .
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
